import timer from '../../utils/timer';
import { Video2dContext, VideoContextErrorCode, VideoGlContext } from '../video-context';
import { Resolution } from '../video-manager';
import { UpdateType, VideoNode, VideoNodeOptions } from './base';
import { SAFARI_VERSION } from '../../utils/environment';
import { filter, fromEvent, Observable, pipe, subject, Subscribe, subscribe, switchMapTo, take, takeUntil } from 'fastrx';
import { mcTimeout } from '../../utils/rx';
import RtcError from '../../common/rtc-error';
import ErrorCode from '../../common/error-code';
import { ILogger, LocalTrackEvent, TIMER_TYPE } from '../..';
import { TextSourceNode, VideoImageSourceNode, VideoTrackSourceNode } from './source';
import type { VideoMixNode } from './mix';
import { VideoTransformNode } from './transform';
import { detectImageType } from '../../utils';

/**
 * 翻转：根据屏幕的坐标系统，需要使用翻转的坐标系
 *
 * 0,0---------1,0
 * |             |
 * |             |
 * |             |
 * 0,1---------1,1
 */
// const texCoords = [
//   0, 1, // 左下
//   0, 0, // 左上
//   1, 1, // 右下
//   1, 0 // 右上
// ];

const texCoords = [
  0, 1, // 左上
  1, 1, // 右上
  0, 0, // 左下
  1, 0 // 右下
];

export class VideoDestinationNode<S extends TexImageSource = HTMLCanvasElement | OffscreenCanvas, C extends Video2dContext | VideoGlContext = Video2dContext> extends VideoNode<S, C> {
  private _intervalId = 0;
  private _sequence = 0;
  checkGLError = false;
  private _useTimerOnShowSubject = subject<void>();
  protected _closedOb = fromEvent(this, 'closed');
  checkVisibilityChange: () => void;
  protected _workerDriven = false; // Whether rendering is driven by Worker's internal timer
  private _frameExtractIntervalId = 0; // Main thread timer for extracting frames and sending to Worker
  private _lastLayoutHash = ''; // Cached layout hash to avoid redundant layout updates to Worker
  private _needsMainThreadExtraction = false; // Whether there are dynamic sources (Video/degraded Track) that need main thread timer extraction
  private _pendingStartFrameRate: number | null = null; // Cached frame rate when start() is called before Worker is ready
  private _workerImageSourceIds = new Set<string>(); // Track ImageSource IDs that have been sent to Worker, used for deduplication
  start(lastFrameRate: number) {
    // Check if the context is in Worker rendering mode
    if (this.context instanceof Video2dContext && this.context.isWorkerMode) {
      if (this.context.workerReady) {
        // Worker is ready, start Worker-driven rendering directly
        this._pendingStartFrameRate = null;
        this._startWorkerDriven(lastFrameRate);
      } else {
        // Worker not ready yet — cache the frame rate and register a callback
        this.log.info(`${this.name} start() called but Worker not ready, pending frameRate=${lastFrameRate}`);
        this._pendingStartFrameRate = lastFrameRate;
        this.context.setOnWorkerReadyCallback(() => {
          if (this._pendingStartFrameRate !== null) {
            const pendingFps = this._pendingStartFrameRate;
            this._pendingStartFrameRate = null;
            this.log.info(`${this.name} Worker ready, executing pending start(${pendingFps})`);
            // start() → _startWorkerDriven() will handle layout sync and static frame push
            this.start(pendingFps);
          }
        });
      }
      return;
    }
    // Fallback: main thread timer-driven rendering
    this._startMainThreadDriven(lastFrameRate);
  }
  /**
   * Worker-driven mode: rendering timer runs inside the Worker.
   * Main thread extracts frames from source nodes and sends them to Worker.
   * Worker runs its own render loop to composite frames.
   */
  private _startWorkerDriven(frameRate: number) {
    // Short-circuit: if Worker-driven mode is already active, only update fps if changed.
    // This avoids unnecessary re-initialization (re-sending ImageSources, re-syncing layout, etc.)
    // which would cause existing static sources (images, text) to flicker or reload.
    if (this._workerDriven) {
      const ctx = this.context as Video2dContext;
      this.log.info(`${this.name} Worker-driven already active, updating fps to ${frameRate}`);
      ctx.postWorkerMessage({ type: 'setFps', fps: frameRate });
      // Ensure main thread timer state is correct (source changes may affect whether
      // main thread frame extraction is needed)
      this._ensureMainThreadTimer();
      return;
    }

    this.log.info(`${this.name} start Worker-driven render ${frameRate} fps`);
    // Stop any existing main thread timer
    timer.clearTask(this._intervalId);
    this._intervalId = 0;
    this._workerDriven = true;
    const ctx = this.context as Video2dContext;

    // Tell Worker to start its internal render loop
    ctx.postWorkerMessage({ type: 'start', fps: frameRate });

    // Start main thread frame extraction timer to feed frames to Worker
    this._startFrameExtraction(frameRate);

    // Always sync layout and push static source frames when entering Worker-driven mode.
    // This handles multiple timing scenarios:
    // 1. Worker was already ready before start() — onWorkerReadyCallback won't fire
    // 2. Worker just became ready — static frames added before Worker ready need pushing
    // 3. Sources added before startMix() — their pushStaticSourceFrame calls were skipped
    const mixNode = this.input as unknown as VideoMixNode;
    if (mixNode && mixNode.inputs) {
      this._syncLayoutToWorker(mixNode, ctx);
      this._pushAllStaticSourceFrames();
      // Fallback: re-send addImageSource for any ImageSource nodes that may have been
      // missed due to Worker not being ready when addImageSource was first called.
      // The buffer mechanism (bufferOrPostImageSourceMessage) handles most cases,
      // but this provides a safety net after _flushPendingState has already run.
      this._resendImageSourcesToWorker(mixNode, ctx);
    }
  }

  /**
   * Start the main thread frame extraction timer.
   * Periodically extracts frames from all source nodes in the mixNode,
   * and sends them along with layout info to the Worker.
   *
   * When Worker frame extraction is supported, Track-type sources are transferred
   * to the Worker via ReadableStream, and only non-Track sources use the main thread timer.
   */
  private _startFrameExtraction(frameRate: number) {
    this._stopFrameExtraction();
    const ctx = this.context as Video2dContext;
    const mixNode = this.input as unknown as VideoMixNode;

    // Attempt Worker-side frame extraction for Track-type sources
    // ctx.useWorkerRendering already guarantees the full Worker pipeline (including frame extraction)
    if (mixNode && mixNode.inputs && ctx.useWorkerRendering) {
      this._setupWorkerFrameExtraction(mixNode, ctx);
    }

    // Use the centralized timer management method
    this._ensureMainThreadTimer();

    // If no main thread extraction needed (all sources are Track-type transferred to Worker or static), just sync layout once
    if (!this._frameExtractIntervalId && mixNode && mixNode.inputs) {
      this._syncLayoutToWorker(mixNode, ctx);
      this.log.info('All sources transferred to Worker, main thread frame extraction timer not needed');
    }
  }

  /**
   * Setup Worker-side frame extraction via ReadableStream for Track-type sources.
   * Non-Track sources (Video) remain extracted on the main thread.
   */
  private _setupWorkerFrameExtraction(mixNode: VideoMixNode, ctx: Video2dContext) {
    let hasVideoSource = false;
    let transferredCount = 0;

    // Register stream error callback for per-source degradation
    ctx.setStreamErrorCallback((id: string, message: string) => {
      this.log.warn(`Source ${id} stream error, degrading to main thread extraction: ${message}`);
      this._onSourceDegradedToMainThread();
    });

    for (const input of mixNode.inputs) {
      if (input.layout.hidden) continue;

      const sourceNode = input.node.input || input.node;
      const id = input.node.sourceId;

      // Check if this is a VideoTrackSourceNode with a valid track
      if (sourceNode instanceof VideoTrackSourceNode) {
        const track = this._getTrackFromSourceNode(sourceNode);
        if (track) {
          const success = ctx.addTrackStream(id, track);
          if (success) {
            transferredCount++;
          }
          // Failed transfers will be detected by ensureMainThreadExtraction via isStreamTransferred check
        }
      } else if (sourceNode instanceof TextSourceNode) {
        // Text source is static — pushed on demand via pushStaticSourceFrame, skip
      } else if (sourceNode instanceof VideoImageSourceNode && sourceNode.image instanceof HTMLImageElement) {
        // Image source (HTMLImageElement) — Worker loads internally via addImageSource message, skip
      } else {
        // Dynamic source (HTMLVideoElement, HTMLCanvasElement, etc.) needs main thread timer
        hasVideoSource = true;
      }
    }

    this._needsMainThreadExtraction = hasVideoSource;

    if (transferredCount > 0) {
      this.log.info(`Worker frame extraction: ${transferredCount} track source(s) transferred, needsMainThreadExtraction=${hasVideoSource}`);
    } else if (!hasVideoSource) {
      this.log.info('Worker frame extraction: all sources are static (Text/Image) or track-based, no main thread timer needed');
    }
  }

  /**
   * Get the MediaStreamTrack from a VideoTrackSourceNode.
   * Walks through the player to access the underlying track.
   */
  private _getTrackFromSourceNode(sourceNode: VideoTrackSourceNode): MediaStreamTrack | null {
    try {
      // VideoTrackSourceNode -> _player -> track
      const player = (sourceNode as any)._player;
      return player?.track || null;
    } catch {
      return null;
    }
  }

  /**
   * Called when a source degrades from Worker ReadableStream to main thread extraction.
   * Ensures the main thread frame extraction timer is running.
   */
  private _onSourceDegradedToMainThread() {
    this._needsMainThreadExtraction = true;
    this._ensureMainThreadTimer();
    this.log.info('Source degraded to main thread extraction');
  }

  /**
   * Stop the main thread frame extraction timer.
   */
  protected _stopFrameExtraction() {
    if (this._frameExtractIntervalId) {
      timer.clearTask(this._frameExtractIntervalId);
      this._frameExtractIntervalId = 0;
    }
  }

  // ===================== Dynamic Source Management for Worker Frame Extraction =====================

  /**
   * Add a newly added Track-type source's ReadableStream to the Worker.
   * Should be called after a new VideoTrackSourceNode is added to the mixNode.
   * @param sourceId The layout-based id (e.g., `nodeName_zIndex`)
   * @param track The MediaStreamTrack to transfer
   */
  addWorkerTrackStream(sourceId: string, track: MediaStreamTrack) {
    if (!this._workerDriven || !(this.context instanceof Video2dContext)) return;
    const ctx = this.context as Video2dContext;
    // ctx.useWorkerRendering already guarantees the full Worker pipeline (including frame extraction)
    if (!ctx.useWorkerRendering) return;

    // Skip if this source's stream has already been transferred
    if (ctx.isStreamTransferred(sourceId)) {
      this.log.info(`addWorkerTrackStream(${sourceId}): already transferred, skipping`);
      return;
    }

    this.log.debug(`addWorkerTrackStream(${sourceId}): track.id=${track.id}, track.label=${track.label}, track.readyState=${track.readyState}`);
    const success = ctx.addTrackStream(sourceId, track);
    if (success) {
      this.log.info(`Dynamic addWorkerTrackStream(${sourceId}): transferred to Worker`);
      // Re-evaluate main thread timer after successful transfer
      this._ensureMainThreadTimer();
    } else {
      // Transfer failed, ensure main thread timer handles it
      this._onSourceDegradedToMainThread();
    }
  }

  /**
   * Remove a source's ReadableStream from the Worker.
   * Should be called when a VideoTrackSourceNode is removed from the mixNode.
   * @param sourceId The layout-based id (e.g., `nodeName_zIndex`)
   */
  removeWorkerTrackStream(sourceId: string) {
    if (!(this.context instanceof Video2dContext)) return;
    const ctx = this.context as Video2dContext;
    if (!ctx.isStreamTransferred(sourceId)) return;

    ctx.removeTrackStream(sourceId);
    this.log.info(`Dynamic removeWorkerTrackStream(${sourceId}): pipeline removed`);
  }

  /**
   * Replace a source's track stream in the Worker.
   * Should be called when replaceTrack is invoked on a VideoTrackSourceNode.
   * @param sourceId The layout-based id
   * @param newTrack The new MediaStreamTrack
   */
  replaceWorkerTrackStream(sourceId: string, newTrack: MediaStreamTrack) {
    if (!(this.context instanceof Video2dContext)) return;
    const ctx = this.context as Video2dContext;
    if (!ctx.isStreamTransferred(sourceId)) {
      this.log.debug(`replaceWorkerTrackStream(${sourceId}): not transferred, skipping`);
      return;
    }

    this.log.debug(`replaceWorkerTrackStream(${sourceId}): replacing with newTrack.id=${newTrack.id}, newTrack.label=${newTrack.label}, newTrack.readyState=${newTrack.readyState}`);
    const success = ctx.replaceTrackStream(sourceId, newTrack);
    if (!success) {
      this.log.warn(`replaceWorkerTrackStream(${sourceId}): new stream transfer failed, degrading`);
      this._onSourceDegradedToMainThread();
    } else {
      this.log.info(`replaceWorkerTrackStream(${sourceId}): stream replaced successfully`);
    }
  }

  /**
   * Public method to trigger layout sync to Worker.
   * Can be called externally (e.g., from _changeMixLayout) to immediately
   * push the latest layout info to the Worker without waiting for the
   * frame extraction timer tick.
   */
  syncLayoutToWorker() {
    if (!(this.context instanceof Video2dContext) || !this.context.isWorkerMode) return;
    const mixNode = this.input as unknown as VideoMixNode;
    if (!mixNode || !mixNode.inputs) return;
    this._syncLayoutToWorker(mixNode, this.context as Video2dContext);
  }

  /**
   * Re-evaluate whether the main thread frame extraction timer should be running.
   * Call this after dynamically adding or removing non-Track sources in Worker mode.
   *
   * - If non-Track sources exist and the timer is not running, start it.
   * - If no non-Track sources remain and the timer is running, stop it.
   * - No-op if not in Worker-driven mode.
   */
  ensureMainThreadExtraction() {
    if (!this._workerDriven) return;
    if (!(this.context instanceof Video2dContext)) return;

    const ctx = this.context as Video2dContext;
    const mixNode = this.input as unknown as VideoMixNode;
    if (!mixNode || !mixNode.inputs) return;

    // Re-count sources that need main thread timer-driven extraction.
    // Static sources (Image/Text) are pushed on demand and don't need the timer.
    // Only dynamic sources (Video/HTMLVideoElement) and non-transferred Track sources need the timer.
    let needsExtraction = false;
    for (const input of mixNode.inputs) {
      if (input.layout.hidden) continue;
      const sourceNode = input.node.input || input.node;
      const id = input.node.sourceId;

      if (sourceNode instanceof VideoTrackSourceNode) {
        // Track source: check if it's actually transferred to Worker; if not, it's degraded
        if (!ctx.isStreamTransferred(id)) {
          needsExtraction = true;
        }
      } else if (sourceNode instanceof TextSourceNode) {
        // Text source is static — pushed on demand, skip
      } else if (sourceNode instanceof VideoImageSourceNode && sourceNode.image instanceof HTMLImageElement) {
        // Image source (HTMLImageElement) — Worker loads internally, skip
      } else {
        // Dynamic source (HTMLVideoElement, HTMLCanvasElement, etc.) needs main thread timer
        needsExtraction = true;
      }
    }

    this._needsMainThreadExtraction = needsExtraction;

    // Use the centralized timer management method
    this._ensureMainThreadTimer();
  }

  /**
   * Push frames for all static sources (Image/Text) currently in the mixNode.
   * Called when Worker becomes ready to ensure static sources that were added
   * before Worker initialization are properly rendered.
   */
  private _pushAllStaticSourceFrames() {
    if (!this._workerDriven) return;
    if (!(this.context instanceof Video2dContext)) return;
    const ctx = this.context as Video2dContext;
    if (!ctx.workerReady) return;

    const mixNode = this.input as unknown as VideoMixNode;
    if (!mixNode || !mixNode.inputs) return;

    let pushedCount = 0;
    for (const input of mixNode.inputs) {
      if (input.layout.hidden) continue;
      const sourceNode = input.node.input || input.node;
      const id = input.node.sourceId;

      const isStaticText = sourceNode instanceof TextSourceNode;
      // In Worker mode, ImageSource is loaded internally by Worker (via addImageSource message),
      // so we skip pushing image frames from main thread.
      const isStaticImage = sourceNode instanceof VideoImageSourceNode && sourceNode.image instanceof HTMLImageElement;

      if (isStaticText) {
        this.pushStaticSourceFrame(id, sourceNode);
        pushedCount++;
      } else if (isStaticImage && !ctx.isWorkerMode) {
        // Only push image frames in non-Worker mode (Worker handles its own image loading)
        this.pushStaticSourceFrame(id, sourceNode);
        pushedCount++;
      }
    }

    if (pushedCount > 0) {
      this.log.info(`_pushAllStaticSourceFrames: pushed ${pushedCount} static source frame(s) after Worker ready`);
    }
  }

  /**
   * Fallback: re-send addImageSource messages to Worker for any ImageSource nodes
   * that exist in the mix graph. This acts as a safety net in case the original
   * addImageSource message was lost (e.g., Worker wasn't ready and buffer was already flushed).
   */
  private _resendImageSourcesToWorker(mixNode: VideoMixNode, ctx: Video2dContext) {
    if (!ctx.isWorkerMode || !ctx.workerReady) return;

    let resentCount = 0;
    let skippedCount = 0;
    for (const input of mixNode.inputs) {
      if (input.layout.hidden) continue;
      const sourceNode = input.node.input || input.node;
      if (!(sourceNode instanceof VideoImageSourceNode) || !(sourceNode.image instanceof HTMLImageElement)) continue;

      const id = input.node.sourceId;
      const imgSrc = (sourceNode.image as HTMLImageElement).src;
      if (!imgSrc) continue;

      // Deduplication: skip if this ImageSource has already been sent to Worker
      if (this._workerImageSourceIds.has(id)) {
        skippedCount++;
        continue;
      }

      const { animated, mimeType } = detectImageType(imgSrc);
      ctx.postWorkerMessage({ type: 'addImageSource', id, src: imgSrc, animated, mimeType });
      this._workerImageSourceIds.add(id);
      resentCount++;
    }

    if (resentCount > 0 || skippedCount > 0) {
      this.log.info(`_resendImageSourcesToWorker: re-sent ${resentCount}, skipped ${skippedCount} (already tracked) ImageSource(s)`);
    }
  }

  /**
   * Push a single static source's frame to the Worker on demand.
   * Used for Image (HTMLImageElement) and Text sources that don't change
   * every frame — we push once on add/update instead of polling via timer.
   *
   * @param sourceId The layout-based id (e.g., `transformNode.name + '_' + zIndex`)
   * @param sourceNode The actual source node (TextSourceNode or VideoImageSourceNode)
   */
  pushStaticSourceFrame(sourceId: string, sourceNode: VideoNode<any>) {
    if (!this._workerDriven) return;
    if (!(this.context instanceof Video2dContext)) return;
    const ctx = this.context as Video2dContext;
    if (!ctx.workerReady) return;

    // Refresh frame content by calling requestFrame on the source node
    try {
      sourceNode.requestFrame(0);
    } catch { /* ignore render errors */ }

    const { image } = sourceNode;
    if (!image) return;

    let width = 0;
    let height = 0;

    try {
      if (image instanceof HTMLImageElement) {
        // Check image readiness to avoid createImageBitmap failure
        if (!image.complete || !image.naturalWidth || !image.naturalHeight) return;
        width = image.naturalWidth;
        height = image.naturalHeight;
      } else if (image instanceof HTMLCanvasElement || image instanceof OffscreenCanvas) {
        ({ width, height } = image);
      } else {
        return; // Unsupported image type for static push
      }

      if (!width || !height) return;

      createImageBitmap(image).then(bitmap => {
        // Re-check Worker readiness after async operation
        if (!ctx.workerReady) {
          bitmap.close();
          return;
        }
        ctx.postWorkerMessage(
          { type: 'updateFrames', frames: [{ id: sourceId, frame: bitmap, width, height }] },
          [bitmap]
        );
      })
        .catch(err => {
          this.log.warn(`pushStaticSourceFrame failed for source ${sourceId}: ${(err as Error).message || err}`);
        });
    } catch {
      // Frame extraction failed, skip
    }
  }

  /**
   * Centralized method to start or stop the main thread frame extraction timer.
   * Uses the single `_needsMainThreadExtraction` flag to decide:
   * - true  → timer needed (Video source or degraded Track source exists)
   * - false → timer not needed (all sources are static or transferred via ReadableStream)
   */
  private _ensureMainThreadTimer() {
    if (!this._workerDriven) return;
    if (!(this.context instanceof Video2dContext)) return;
    const ctx = this.context as Video2dContext;

    if (this._needsMainThreadExtraction && !this._frameExtractIntervalId) {
      // Need timer but not running — check if there are sources to extract
      const mixNode = this.input as unknown as VideoMixNode;
      if (!mixNode || !mixNode.inputs || mixNode.inputs.length === 0) {
        this.log.info('_ensureMainThreadTimer: no sources connected, deferring timer');
        return;
      }
      this._frameExtractIntervalId = timer.run(TIMER_TYPE.INTERVAL_IN_WORKER, () => {
        const currentMixNode = this.input as unknown as VideoMixNode;
        if (!currentMixNode || !currentMixNode.inputs) return;
        this._syncLayoutToWorker(currentMixNode, ctx);
        this._extractAndSendFrames(currentMixNode, ctx);
      }, { fps: this.context.frameRate });
      this.log.info(`_ensureMainThreadTimer: started timer fps=${this.context.frameRate} (needsMainThreadExtraction=${this._needsMainThreadExtraction})`);
    } else if (!this._needsMainThreadExtraction && this._frameExtractIntervalId) {
      // Timer running but no longer needed — stop it
      timer.clearTask(this._frameExtractIntervalId);
      this._frameExtractIntervalId = 0;
      this.log.info('_ensureMainThreadTimer: stopped timer, no dynamic sources need main thread extraction');
    }
  }

  /**
   * Sync layout info from mixNode to Worker.
   * Only sends update when layout has actually changed.
   */
  private _syncLayoutToWorker(mixNode: VideoMixNode, ctx: Video2dContext) {
    // Skip if Worker is not ready yet
    if (!ctx.workerReady) return;

    const layouts = mixNode.inputs.map(input => ({
      id: input.node.sourceId,
      x: input.layout.x ?? input.node.x,
      y: input.layout.y ?? input.node.y,
      width: input.layout.width ?? input.node.width,
      height: input.layout.height ?? input.node.height,
      zIndex: input.layout.zIndex,
      fillMode: input.layout.fillMode,
      rotation: input.layout.rotation,
      // In Worker mode, TransformNode rendering is skipped, so mirror must be forwarded to Worker
      mirror: input.node instanceof VideoTransformNode ? input.node.mirror : false,
      hidden: !!input.layout.hidden
    }));

    const layoutHash = JSON.stringify(layouts);
    if (layoutHash !== this._lastLayoutHash) {
      this._lastLayoutHash = layoutHash;
      ctx.postWorkerMessage({ type: 'updateLayout', layouts });
    }
  }

  /**
   * Extract frames from visible source nodes and send to Worker.
   * Skips sources that have been transferred to the Worker via ReadableStream.
   * Uses requestFrame to refresh source state, then extracts transferable frame data.
   */
  private _extractAndSendFrames(mixNode: VideoMixNode, ctx: Video2dContext) {
    // Skip frame extraction if Worker is not ready yet to avoid wasting resources
    if (!ctx.workerReady) return;

    const frames: Array<{ id: string; frame: ImageBitmap | VideoFrame; width: number; height: number }> = [];
    const transfers: Transferable[] = [];

    for (const input of mixNode.inputs) {
      if (input.layout.hidden) continue;

      const id = input.node.sourceId;

      // Skip sources that have been transferred to Worker via ReadableStream
      if (ctx.isStreamTransferred(id)) {
        continue;
      }

      // Walk back from the transform node to find the actual source node
      const sourceNode = input.node.input || input.node;

      // Skip static sources (Image/Text) — they are pushed on demand via pushStaticSourceFrame
      if (sourceNode instanceof TextSourceNode) {
        continue;
      }
      if (sourceNode instanceof VideoImageSourceNode && sourceNode.image instanceof HTMLImageElement) {
        continue;
      }

      // Refresh frame state by calling requestFrame on the source node.
      // We intentionally call sourceNode.requestFrame (not input.node.requestFrame)
      // because the Worker handles transform (mirror/rotation) via drawFrame,
      // and we just need the source's raw rendered image.
      try {
        sourceNode.requestFrame(0);
      } catch { /* ignore render errors */ }

      // Use sourceNode's image directly. In Worker mode, transforms are applied
      // by the Worker's drawFrame function based on layout info, so we skip
      // TransformNode rendering on the main thread.
      const { image } = sourceNode;
      if (!image) continue;

      try {
        let extractedFrame: ImageBitmap | VideoFrame | null = null;
        let width = 0;
        let height = 0;

        if (image instanceof HTMLVideoElement) {
          ({ videoWidth: width, videoHeight: height } = image);
          if (!width || !height || image.readyState < 2) continue;
          // Use VideoFrame for zero-copy transfer (Chrome 94+)
          if (typeof VideoFrame !== 'undefined') {
            extractedFrame = new VideoFrame(image, { timestamp: 0 });
          } else if (typeof createImageBitmap !== 'undefined') {
            // createImageBitmap is async, we handle it separately
            createImageBitmap(image).then(bitmap => {
              if (!ctx.workerReady) {
                bitmap.close(); return;
              }
              ctx.postWorkerMessage(
                { type: 'updateFrames', frames: [{ id, frame: bitmap, width, height }] },
                [bitmap]
              );
            })
              .catch(err => {
                this.log.warn(`createImageBitmap failed for source ${id} (video): ${(err as Error).message || err}`);
              });
            continue;
          }
        } else if (image instanceof HTMLCanvasElement || image instanceof OffscreenCanvas) {
          ({ width, height } = image);
          if (!width || !height) continue;
          if (typeof createImageBitmap !== 'undefined') {
            createImageBitmap(image).then(bitmap => {
              if (!ctx.workerReady) {
                bitmap.close(); return;
              }
              ctx.postWorkerMessage(
                { type: 'updateFrames', frames: [{ id, frame: bitmap, width, height }] },
                [bitmap]
              );
            })
              .catch(err => {
                this.log.warn(`createImageBitmap failed for source ${id} (canvas): ${(err as Error).message || err}`);
              });
            continue;
          }
        } else if (image instanceof HTMLImageElement) {
          // Use naturalWidth/naturalHeight and check complete for image readiness
          if (!image.complete || !image.naturalWidth || !image.naturalHeight) continue;
          width = image.naturalWidth;
          height = image.naturalHeight;
          if (typeof createImageBitmap !== 'undefined') {
            createImageBitmap(image).then(bitmap => {
              if (!ctx.workerReady) {
                bitmap.close(); return;
              }
              ctx.postWorkerMessage(
                { type: 'updateFrames', frames: [{ id, frame: bitmap, width, height }] },
                [bitmap]
              );
            })
              .catch(err => {
                this.log.warn(`createImageBitmap failed for source ${id} (image): ${(err as Error).message || err}`);
              });
            continue;
          }
        }

        if (extractedFrame && width && height) {
          frames.push({ id, frame: extractedFrame, width, height });
          transfers.push(extractedFrame);
        }
      } catch {
        // Frame extraction failed, skip this source
        continue;
      }
    }

    // Send all synchronously extracted frames in a single batch message
    if (frames.length > 0) {
      if (!ctx.workerReady) {
        // Worker became unavailable, release all extracted frames to avoid memory leak
        for (const f of frames) {
          if ('close' in f.frame && typeof f.frame.close === 'function') f.frame.close();
        }
        return;
      }
      ctx.postWorkerMessage({ type: 'updateFrames', frames }, transfers);
    }
  }
  /**
   * Main thread timer-driven mode (original logic).
   */
  protected _startMainThreadDriven(lastFrameRate: number) {
    this.log.info(`${this.name} start render ${lastFrameRate} fps`);
    this._workerDriven = false;
    timer.clearTask(this._intervalId);
    this._intervalId = timer.run(TIMER_TYPE.INTERVAL_IN_WORKER, () => {
      if (lastFrameRate !== this.context.frameRate) {
        timer.clearTask(this._intervalId);
        this.start(this.context.frameRate);
      }
      this.requestFrame(this._sequence++);
      if (this.checkGLError && this.context instanceof VideoGlContext) {
        const gl = this.context.ctx as WebGL2RenderingContext;
        const error = gl.getError();
        if (error) {
          this.context.destroy(new RtcError({
            code: ErrorCode.VIDEO_MANAGER_ERROR,
            extraCode: VideoContextErrorCode.WEBGL_ERROR,
            message: `${this.name} req ${this._sequence} render ${this.totalFrames} faild ${error}`
          }));
        }
      }
    }, { fps: this.context.frameRate });
  }
  constructor(context: C, options?: VideoNodeOptions) {
    super(context, Object.assign({ useDefaultProgram: true, createTexture: false, name: 'destination' }, options));
    if (context instanceof Video2dContext) this.ctx2d = context.ctx || null;
    else if (context.available && options?.mirrorUpAndDown) this.setTexBuffer(texCoords);
    pipe(
      this._useTimerOnShowSubject,
      switchMapTo(fromEvent(document, 'visibilitychange')),
      takeUntil(this._closedOb),
      subscribe(() => {
        if (document.hidden) {
          this.start(this.context.frameRate);
          this.log.info(`${this.name} use timer`);
        }
      })
    );
  }
  render(seq: number) {
    if (this.input?.requestFrame(seq)) {
      this.useProgram();
      this.useBufferFrame();
      this.useInputTexture();
      this.draw();
      this.emit(VideoNode.RENDER, this.context._canvas!);
      return true;
    }
    return false;
  }
  addInput<T extends VideoNode<S, C>>(node: T, ...args: any[]): void {
    super.addInput(node, ...args);
    this.start(this.context.frameRate);
  }
  update(type: UpdateType = UpdateType.JustUpdate) {
    if (this.state === 'closed') return;
    if (this._intervalId) {
      timer.clearTask(this._intervalId);
      this._intervalId = 0;
      if (type === UpdateType.UseTimerOnShow) {
        this.log.info(`${this.name} use requestVideoFrameCallback`);
        this._useTimerOnShowSubject.next();
      }
    }
    this.requestFrame(this._sequence++);
  }
  removeInput(node: VideoNode<S, C>): void {
    super.removeInput(node);
    timer.clearTask(this._intervalId);
    this._cleanupWorkerDrivenState();
  }
  resize(width: number, height: number) {
    super.resize(width, height);
    this.context.setSize(width, height);
  }

  /**
   * Whether the Worker-driven rendering mode is currently active.
   */
  get isWorkerDriven(): boolean {
    return this._workerDriven;
  }

  /**
   * Track an ImageSource ID that has been sent to Worker.
   * Called by external code (e.g., bufferOrPostImageSourceMessage) when an addImageSource
   * message is posted to Worker, so _resendImageSourcesToWorker can skip already-tracked sources.
   */
  trackWorkerImageSource(id: string) {
    this._workerImageSourceIds.add(id);
  }

  /**
   * Untrack an ImageSource ID when it is removed from Worker.
   * Called by external code when a removeImageSource message is posted to Worker.
   */
  untrackWorkerImageSource(id: string) {
    this._workerImageSourceIds.delete(id);
  }

  close() {
    super.close();
    timer.clearTask(this._intervalId);
    this._cleanupWorkerDrivenState();
    document.removeEventListener('visibilitychange', this.checkVisibilityChange);
  }

  /**
   * Clean up Worker-driven rendering state.
   * Shared by removeInput() and close() to avoid code duplication.
   * Clears pending start state, Worker ready callback, stops frame extraction,
   * cleans up transferred streams, and stops the Worker render loop.
   */
  private _cleanupWorkerDrivenState() {
    // Clean up pending start state and Worker ready callback
    this._pendingStartFrameRate = null;
    if (this.context instanceof Video2dContext) {
      this.context.setOnWorkerReadyCallback(null);
    }
    // Stop Worker render loop and frame extraction
    if (this._workerDriven && this.context instanceof Video2dContext) {
      this._stopFrameExtraction();
      // Clean up all transferred streams
      this._cleanupAllWorkerStreams();
      this.context.postWorkerMessage({ type: 'stop' });
      this._workerDriven = false;
      this._workerImageSourceIds.clear();
    }
  }

  /**
   * Clean up all Worker-transferred ReadableStream pipelines.
   */
  protected _cleanupAllWorkerStreams() {
    if (!(this.context instanceof Video2dContext)) return;
    const ctx = this.context as Video2dContext;
    // Remove each transferred stream via Worker message
    for (const streamId of ctx.transferredStreams) {
      ctx.removeTrackStream(streamId);
    }
    this._needsMainThreadExtraction = false;
    ctx.setStreamErrorCallback(null);
  }
}
export class VideoTrackDestination<C extends Video2dContext | VideoGlContext = Video2dContext> extends VideoDestinationNode<TexImageSource, C> {
  private _videoTrack?: MediaStreamVideoTrack;
  private _muteOb: Observable<Event>;
  private _subscription?: Subscribe<Event>;
  private _canvasContainer?: HTMLDivElement;// 用于 safari 版本<17 时 videoTrack 没有输出帧，需要将 canvas 放到 dom 中
  private _useTrackGenerator = false;
  private _trackGeneratorDegradedHandler: (() => void) | null = null;
  constructor(context: C, options?: VideoNodeOptions) {
    super(context, options);
    if (Number(SAFARI_VERSION) < 17) {
      this._canvasContainer = document.createElement('div');
      this._canvasContainer.style.display = 'none';
    }

    // Determine track source: TrackGenerator or captureStream
    if (context instanceof Video2dContext && context.useTrackGenerator) {
      // TrackGenerator mode: use the generator track directly (no captureStream needed)
      this._useTrackGenerator = true;
      this._videoTrack = context.generatorTrack as unknown as MediaStreamVideoTrack;
      this.log.info(`VideoTrackDestination using TrackGenerator track, id=${this._videoTrack.id}, readyState=${this._videoTrack.readyState}`);

      // Listen for degradation event from Video2dContext
      this._trackGeneratorDegradedHandler = () => {
        this._handleTrackGeneratorDegradation();
      };
      context.on('trackGeneratorDegraded' as any, this._trackGeneratorDegradedHandler);
    } else {
      // Fallback: captureStream mode
      [this._videoTrack] = context.canvas!.captureStream().getVideoTracks();
      this.log.info(`VideoTrackDestination using captureStream track, id=${this._videoTrack.id}, readyState=${this._videoTrack.readyState}`);
    }

    this._muteOb = fromEvent(this._videoTrack, 'mute');
    pipe(fromEvent(this._videoTrack, 'ended'), takeUntil(this._closedOb), subscribe(() => {
      this.context.destroy(new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.CANVAS_TRACK_ENDED,
        message: 'video track ended'
      }));
    }));
  }

  /**
   * Handle complete Worker→main thread degradation.
   * Called when Video2dContext emits 'trackGeneratorDegraded' event, meaning
   * Worker mode has been completely shut down and a new main thread 2d canvas is ready.
   * We need to: switch to captureStream track, clean up Worker state, restart main thread rendering.
   */
  private _handleTrackGeneratorDegradation() {
    if (!this._useTrackGenerator) return;
    this.log.warn('VideoTrackDestination: Worker mode degraded, switching to main thread captureStream');

    this._useTrackGenerator = false;

    // Remove degradation listener
    if (this._trackGeneratorDegradedHandler && this.context instanceof Video2dContext) {
      this.context.off('trackGeneratorDegraded' as any, this._trackGeneratorDegradedHandler);
      this._trackGeneratorDegradedHandler = null;
    }

    // Clean up Worker-driven state in the destination node
    this._stopFrameExtraction();
    this._cleanupAllWorkerStreams();
    this._workerDriven = false;

    // Get new track from the new main thread canvas via captureStream
    const [newTrack] = this.context.canvas!.captureStream().getVideoTracks();
    if (!newTrack) {
      this.log.error('VideoTrackDestination: Failed to get captureStream track during degradation');
      return;
    }

    // Replace video track
    const oldTrack = this._videoTrack;
    this._videoTrack = newTrack;
    this.log.info(`VideoTrackDestination degraded to main thread captureStream track, id=${newTrack.id}, readyState=${newTrack.readyState}`);

    // Re-bind mute and ended listeners
    this._muteOb = fromEvent(this._videoTrack, 'mute');
    pipe(fromEvent(this._videoTrack, 'ended'), takeUntil(this._closedOb), subscribe(() => {
      this.context.destroy(new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.CANVAS_TRACK_ENDED,
        message: 'video track ended'
      }));
    }));

    // Restart main thread rendering timer
    this._startMainThreadDriven(this.context.frameRate);

    // Emit trackChanged event so LocalMixVideoTrack can update its output track
    this.emit('trackChanged' as any, newTrack);
  }

  enableCheckMute() {
    this._subscription = pipe(this._muteOb, takeUntil(this._closedOb), switchMapTo(mcTimeout(5000)), filter(() => !!this._videoTrack?.muted && !document.hidden), subscribe(() => {
      this.context.destroy(new RtcError({
        code: ErrorCode.VIDEO_MANAGER_ERROR,
        extraCode: VideoContextErrorCode.CANVAS_TRACK_MUTED,
        message: 'video track muted'
      }));
    }));
  }
  disableCheckMute() {
    this._subscription?.dispose();
  }
  get videoTrack() {
    return this._videoTrack;
  }
  putCanvasIntoDom() {
    if (!this.context._canvas || !this._canvasContainer) return;
    if (!document.getElementById(this.context._canvas.id)) {
      this.log.info(`${this.name} put canvas to body`);
      document.body.appendChild(this._canvasContainer);
      this._canvasContainer.appendChild(this.context._canvas);
    }
  }
  render(seq: number) {
    this.putCanvasIntoDom();
    return super.render(seq);
  }
  render2d(seq: number) {
    this.putCanvasIntoDom();
    return super.render2d(seq);
  }
  close() {
    // Remove degradation listener
    if (this._trackGeneratorDegradedHandler && this.context instanceof Video2dContext) {
      this.context.off('trackGeneratorDegraded' as any, this._trackGeneratorDegradedHandler);
      this._trackGeneratorDegradedHandler = null;
    }

    super.close();
    this._videoTrack?.stop();
    delete this._videoTrack;
    this._canvasContainer?.remove();
  }
}
export class VideoTrackDestination4allin1 extends VideoTrackDestination<VideoGlContext> {
  render(seq: number) {
    const result = !!this.input?.requestFrame(seq);
    if (this.context._canvas2d) {
      const ctx = this.context._canvas2d.getContext('2d')!;
      ctx.clearRect(0, 0, this.context._canvas2d.width, this.context._canvas2d.height);
      ctx.drawImage(this.context._canvas!, 0, 0, this.context._canvas2d.width, this.context._canvas2d.height);
      this.emit(VideoNode.RENDER, this.context._canvas2d);
    } else {
      this.emit(VideoNode.RENDER, this.context._canvas!);
    }
    return result;
  }
}

export class VideoFrameDestination extends VideoDestinationNode {
  getVideoFrame() {
    return new VideoFrame(this.context._canvas!);
  }
}

export class SmallVideoTrackDestination extends VideoTrackDestination {
  constructor(context: Video2dContext, public resolution: Resolution, logger: ILogger) {
    super(context, { name: 'smallDestination', logger });
  }
  resize(width: number, height: number) {
    let ratio;
    const bigResolution = width * height;
    const smallResolution = this.resolution.width * this.resolution.height;
    // 如果大流是640 * 480，小流尺寸是 1080 * 720，即不满足
    this.log.info(`big res: ${width}*${height} small res: ${this.resolution.width}*${this.resolution.height} `);
    if (bigResolution > smallResolution) {
      ratio = bigResolution / smallResolution;
    } else {
      this.log.warn(`Small stream resolution is not smaller than big stream, which is invalid. big: ${width} * ${height} small: ${this.resolution.width} * ${this.resolution.height}`);
      ratio = bigResolution / (160 * 120);
    }
    super.resize(width / Math.sqrt(ratio), height / Math.sqrt(ratio));
  }
}
